import React, { useCallback, useMemo, useRef, useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  Skeleton,
  Alert,
  Button,
  IconButton,
  Tooltip,
  ToggleButton,
  ToggleButtonGroup,
  FormControlLabel,
  Switch,
} from '@mui/material';
import {
  Fullscreen as FullscreenIcon,
  PlayArrow as PlayIcon,
  Stop as StopIcon,
  ZoomIn as ZoomInIcon,
  DragIndicator as DragIndicatorIcon,
} from '@mui/icons-material';
import { camerasApi, Camera, CameraListResponse } from '../services/camerasApi';
import {
  startStream as mjpegStartStream,
  stopStream as mjpegStopStream,
  getSnapshot as mjpegGetSnapshot,
  isLikelyRealMjpegSnapshot,
  MJPEG_LIVE_READY_SNAPSHOT_STREAK,
} from '../services/mjpegApi';
import { preferredMjpegModeForCamera } from '../services/mjpegMode';
import { api, ensureUiScope } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { canUseAdvancedIncidentsUi, isProductEvaluator, isSandboxEhsEvaluator } from '../auth/access';
import { useMode } from '../contexts/ModeContext';
import { RefreshIconButton } from '../components/RefreshIconButton';
import { LiveViewCameraPatternPanel } from '../components/liveView/LiveViewCameraPatternPanel';
import AnnotatedEvidencePanel from '../components/liveView/AnnotatedEvidencePanel';
import { LiveViewFullscreenDialog } from '../components/liveView/LiveViewFullscreenDialog';
import ContainedMediaOverlay from '../components/liveView/ContainedMediaOverlay';
import {
  cameraEvidenceStatusMeta,
  cameraLastFrameTimestamp,
  formatCameraLastFrameLabel,
  isCameraOfflineForDisplay,
  shouldDisplayCameraInLiveView,
} from '../domain/overview/overviewCameraEvidence';
import {
  LIVE_VIEW_CAMERA_GRID_FILL_CLASS,
  LIVE_VIEW_HELP_LINE,
  LIVE_VIEW_PAGE_SUBTITLE,
  LIVE_VIEW_PAGE_TITLE,
  LIVE_VIEW_START_LABEL,
  LIVE_VIEW_STOP_LABEL,
  liveViewCameraStatusLine,
} from '../domain/liveView/liveViewPatternDisplay';
import { lookupLiveViewCameraPatternLink, selectLiveViewGridCameras } from '../domain/liveView/liveViewGridCameras';
import { useLiveViewPatternContext } from '../hooks/useLiveViewPatternContext';
import { useIncidentsUiMode } from './incidents/useIncidentsUiMode';
import { getWarehouseCapabilityProfile } from '../utils/warehouseNavigation';
import { scheduleDeferredWork } from '../utils/deferredWork';

type StreamState = { url: string | null; loading: boolean; error: string | null };
type ViewKind = 'live' | 'annotated';
type CameraUnavailableState = { title: string; message: string; detail?: string } | null;
const ANNOTATED_RECOVERY_COOLDOWN_MS = 2 * 60 * 1000;
const ANNOTATED_INITIAL_RECOVERY_WAIT_MS = 20_000;
const ANNOTATED_POST_RECOVERY_WAIT_MS = 45_000;
const OFFSCREEN_RELEASE_GRACE_MS = 20_000;
/** Unfiltered Live View auto-starts only the first tile; additional tiles use Start (saves edge/cloud bandwidth). */
const LIVE_VIEW_AUTO_START_BUDGET = 1;
const LIVE_VIEW_FILTERED_AUTO_START_BUDGET = 4;
const ENABLE_DEBUG_ANNOTATED_LIVE =
  process.env.REACT_APP_ENABLE_DEBUG_ANNOTATED_LIVE === 'true';

export function usesAnnotatedEvidenceMode(viewKind: ViewKind): boolean {
  return viewKind === 'annotated' && !ENABLE_DEBUG_ANNOTATED_LIVE;
}

export function usesAnnotatedLiveStream(viewKind: ViewKind): boolean {
  return viewKind === 'annotated' && ENABLE_DEBUG_ANNOTATED_LIVE;
}

async function recoverAnnotatedCamera(
  cameraId: string,
  deviceId: string | undefined,
  annotatedRecoveryRef: React.MutableRefObject<Record<string, number>>,
): Promise<boolean> {
  const normalizedDeviceId = String(deviceId || '').trim();
  if (!normalizedDeviceId) return false;
  const recoveryKey = mjpegRowKey(cameraId, normalizedDeviceId, 'annotated');
  const lastAttemptMs = annotatedRecoveryRef.current[recoveryKey] || 0;
  const nowMs = Date.now();
  if ((nowMs - lastAttemptMs) < ANNOTATED_RECOVERY_COOLDOWN_MS) {
    return false;
  }
  annotatedRecoveryRef.current[recoveryKey] = nowMs;

  try {
    await mjpegStopStream(cameraId, normalizedDeviceId, 'annotated');
  } catch {
    // Best-effort: clearing the stale annotated MJPEG session should not block recovery.
  }

  let stopOk = false;
  try {
    const stopRes = await api.post(
      `/api/v1/pipeline/devices/${encodeURIComponent(normalizedDeviceId)}/edge/pipelines/cameras/${encodeURIComponent(cameraId)}/stop`,
      { reason: 'live_view_annotated_recovery', ttl_sec: 15 },
    );
    stopOk = stopRes?.data?.ok !== false;
  } catch {
    stopOk = false;
  }

  await new Promise((r) => setTimeout(r, 1500));

  try {
    const startRes = await api.post(
      `/api/v1/pipeline/devices/${encodeURIComponent(normalizedDeviceId)}/edge/pipelines/cameras/${encodeURIComponent(cameraId)}/start`,
      { reason: 'live_view_annotated_recovery' },
    );
    return startRes?.data?.ok !== false || stopOk;
  } catch {
    return stopOk;
  }
}

/** Match cloud_api mjpeg_session_key: isolate streams when the same camera_id exists on multiple devices. */
function cameraRowKey(cameraId: string, edgeDeviceId?: string): string {
  const d = (edgeDeviceId || '').trim();
  return d ? `${d}::${cameraId}` : cameraId;
}

function liveViewTileOrderStorageKey(siteId: string): string {
  const s = String(siteId || '').trim() || 'default';
  return `liveView.tileOrder.v1:${s}`;
}

function liveViewViewKindStorageKey(siteId: string): string {
  const s = String(siteId || '').trim() || 'default';
  return `liveView.viewKinds.v1:${s}`;
}

function mergeTileOrder(prev: string[], currentKeys: string[]): string[] {
  const set = new Set(currentKeys);
  const next = prev.filter((k) => set.has(k));
  for (const k of currentKeys) {
    if (!next.includes(k)) next.push(k);
  }
  return next;
}

function reorderRowKeys(order: string[], from: string, to: string): string[] {
  const i = order.indexOf(from);
  const j = order.indexOf(to);
  if (i < 0 || j < 0 || i === j) return order;
  const next = [...order];
  next.splice(i, 1);
  next.splice(j, 0, from);
  return next;
}

function loadSavedTileOrder(siteId: string): string[] {
  try {
    const raw = localStorage.getItem(liveViewTileOrderStorageKey(siteId));
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((x): x is string => typeof x === 'string');
  } catch {
    return [];
  }
}

function persistTileOrder(siteId: string, order: string[]): void {
  try {
    localStorage.setItem(liveViewTileOrderStorageKey(siteId), JSON.stringify(order));
  } catch {
    /* ignore quota / private mode */
  }
}

function loadSavedViewKinds(siteId: string): Record<string, ViewKind> {
  try {
    const raw = localStorage.getItem(liveViewViewKindStorageKey(siteId));
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {};
    const out: Record<string, ViewKind> = {};
    Object.entries(parsed).forEach(([key, value]) => {
      if (value === 'live' || value === 'annotated') out[key] = value;
    });
    return out;
  } catch {
    return {};
  }
}

function persistViewKinds(siteId: string, viewKinds: Record<string, ViewKind>): void {
  try {
    localStorage.setItem(liveViewViewKindStorageKey(siteId), JSON.stringify(viewKinds));
  } catch {
    /* ignore quota / private mode */
  }
}

function mjpegRowKey(cameraId: string, edgeDeviceId?: string, viewKind: ViewKind = 'live'): string {
  return `${cameraRowKey(cameraId, edgeDeviceId)}::${viewKind}`;
}

function cameraSupportsAnnotated(camera: Camera): boolean {
  const overridePipeline = String((camera as any)?.pipeline_override || '').trim().toLowerCase();
  const pipeline = String((camera as any)?.pipeline || '').trim().toLowerCase();
  const effectivePipeline = overridePipeline || pipeline;
  return !effectivePipeline.includes('rtsp_simple');
}

function resolvedViewKind(camera: Camera, preferred?: ViewKind): ViewKind {
  const next = preferred || 'live';
  if (next === 'annotated' && !cameraSupportsAnnotated(camera)) return 'live';
  return next;
}

function withFreshCacheBuster(url: string): string {
  const next = String(url || '');
  if (!next) return next;
  const stamp = `_=${Date.now()}`;
  if (/[?&]_=\d+/.test(next)) {
    return next.replace(/([?&])_=\d+/, `$1${stamp}`);
  }
  return `${next}${next.includes('?') ? '&' : '?'}${stamp}`;
}

function parseMjpegStreamKey(key: string): { cameraId: string; deviceId?: string; viewKind: ViewKind } {
  const parts = key.split('::');
  if (parts.length >= 3) {
    const viewKind = parts[parts.length - 1] === 'annotated' ? 'annotated' : 'live';
    const cameraId = parts[parts.length - 2] || '';
    const deviceId = parts.slice(0, -2).join('::') || undefined;
    return { cameraId, deviceId, viewKind };
  }
  if (parts.length === 2) {
    return { deviceId: parts[0] || undefined, cameraId: parts[1] || '', viewKind: 'live' };
  }
  return { cameraId: key, viewKind: 'live' };
}

const viewLabel = (viewKind: ViewKind): string => (
  viewKind === 'annotated' ? 'Annotated Evidence' : 'Live View'
);

export function formatLiveViewDialogTitle(cameraLabel: string, viewKind: ViewKind): string {
  return `${cameraLabel} - ${viewLabel(viewKind)}`;
}

function formatTimeAgo(timestamp?: string | null): string | null {
  const raw = String(timestamp || '').trim();
  if (!raw) return null;
  const value = new Date(raw).getTime();
  if (!Number.isFinite(value)) return null;
  const diffMs = Math.max(0, Date.now() - value);
  const seconds = Math.round(diffMs / 1000);
  if (seconds < 10) return 'just now';
  if (seconds < 60) return `${seconds}s ago`;
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  return `${days}d ago`;
}

export function getCameraUnavailableState(
  camera: Camera,
  viewKind: ViewKind = 'live',
): CameraUnavailableState {
  if (camera.enabled === false || camera.status === 'disabled') {
    return {
      title: 'Camera disabled',
      message: `${viewLabel(viewKind)} is unavailable until this camera is enabled again.`,
    };
  }

  const healthState = String(camera.health_state?.state || '').trim().toUpperCase();
  const status = String(camera.status || '').trim().toLowerCase();
  const runtimeHealthStatus = String(camera.health?.status || '').trim().toLowerCase();
  const lastSeen = formatTimeAgo(
    camera.health_state?.device_last_seen || camera.health?.last_frame || camera.updated_at || null,
  );

  // Runtime-reported "ready/running" is more trustworthy than stale heartbeat-
  // derived OFFLINE/AUTH/STREAM summaries for whether Live View should try the
  // stream path. Let MJPEG start/snapshot probing be the source of truth.
  if (status === 'running' || status === 'ready' || runtimeHealthStatus === 'running' || runtimeHealthStatus === 'ready') {
    return null;
  }

  if (status === 'offline' || healthState === 'OFFLINE') {
    return {
      title: 'Device offline',
      message: `This camera is currently offline. ${viewLabel(viewKind)} is unavailable.`,
      detail: lastSeen ? `Last seen ${lastSeen}.` : undefined,
    };
  }

  if (healthState === 'AUTH_FAILED') {
    return {
      title: 'Camera authentication failed',
      message: `${viewLabel(viewKind)} is unavailable until the camera credentials are fixed.`,
      detail: lastSeen ? `Last healthy contact ${lastSeen}.` : undefined,
    };
  }

  if (healthState === 'STREAM_ERROR') {
    return {
      title: 'Stream unavailable',
      message: `${viewLabel(viewKind)} could not be retrieved from the device right now.`,
      detail: lastSeen ? `Last good activity ${lastSeen}.` : undefined,
    };
  }

  return null;
}

export function formatPoweredOffHiddenLabel(count: number): string {
  return `${count.toLocaleString()} camera${count === 1 ? '' : 's'} on powered-off units hidden`;
}

const LiveView: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { mode } = useMode();
  const { effectiveUser: user } = useAuth();
  const readOnlyEvaluationFeed = isProductEvaluator(user);
  const sandboxEvaluationFeed = isSandboxEhsEvaluator(user);
  const capabilityProfile = useMemo(() => getWarehouseCapabilityProfile(user), [user]);
  const incidentsAdvancedEligible = useMemo(
    () => canUseAdvancedIncidentsUi(capabilityProfile.capabilities),
    [capabilityProfile.capabilities],
  );
  const { incidentsUiMode } = useIncidentsUiMode(user?.id, incidentsAdvancedEligible);
  const showTechnicalIds = incidentsUiMode === 'advanced';
  const [cameras, setCameras] = useState<Camera[]>([]);
  const [bootstrapSummary, setBootstrapSummary] = useState<CameraListResponse['summary'] | null>(null);
  const [loading, setLoading] = useState(true);
  const [bootstrapSlow, setBootstrapSlow] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [streams, setStreams] = useState<Record<string, StreamState>>({});
  const [posterMap, setPosterMap] = useState<Record<string, string>>({});
  // Track started streams (device::camera when device is known) so we don't churn on list refresh.
  const startedStreamKeysRef = useRef<Set<string>>(new Set());
  // Explicitly stopped stream keys so auto-start doesn't bring them back.
  const stoppedStreamKeysRef = useRef<Set<string>>(new Set());
  // Manual Start Live View holds (second+ tiles). Must not be auto-released after the
  // auto-start budget grace timer — that left Start disabled with only a poster.
  const manualHoldKeysRef = useRef<Set<string>>(new Set());
  const [stoppedStreamKeys, setStoppedStreamKeys] = useState<Set<string>>(new Set());
  const [switchingCameraKeys, setSwitchingCameraKeys] = useState<Set<string>>(new Set());
  const posterLoadingKeysRef = useRef<Set<string>>(new Set());
  const releaseTimersRef = useRef<Record<string, number>>({});
  // Track retry attempts/timers per camera for self-healing live view (token expiry, transient RTSP failures, etc).
  const retryStateRef = useRef<Record<string, { attempt: number; timerId: number | null }>>({});
  // Per-camera nonce to avoid stale async "wait for first frame" loops overwriting newer state.
  const startNonceRef = useRef<Record<string, number>>({});
  const annotatedRecoveryRef = useRef<Record<string, number>>({});

  // Large viewer (zoom/pan)
  const [viewerOpen, setViewerOpen] = useState(false);
  const [viewerStreamKey, setViewerStreamKey] = useState<string | null>(null);
  const [viewerTitle, setViewerTitle] = useState<string>('Live View');
  const [viewKindByCameraKey, setViewKindByCameraKey] = useState<Record<string, ViewKind>>({});
  /** Row keys (`device::camera_id`) in display order; persisted per site in localStorage. */
  const [tileRowOrder, setTileRowOrder] = useState<string[]>([]);
  const [tileDragKey, setTileDragKey] = useState<string | null>(null);
  const [tileDragOverKey, setTileDragOverKey] = useState<string | null>(null);
  const [viewerFullScreen, setViewerFullScreen] = useState<boolean>(false);
  const [zoom, setZoom] = useState<number>(1);
  const [pan, setPan] = useState<{ x: number; y: number }>({ x: 0, y: 0 });
  const [showOfflineCameras, setShowOfflineCameras] = useState(false);
  const tileViewportNodesRef = useRef<Record<string, HTMLDivElement | null>>({});
  const autoStartRowKeysRef = useRef<Set<string>>(new Set());
  const viewerStreamKeyRef = useRef<string | null>(null);
  const draggingRef = useRef<{ dragging: boolean; startX: number; startY: number; baseX: number; baseY: number }>({
    dragging: false,
    startX: 0,
    startY: 0,
    baseX: 0,
    baseY: 0,
  });

  const cameraIdParam = searchParams.get('camera_id');
  const deviceIdParam = (searchParams.get('device_id') || '').trim() || undefined;
  const siteIdParam = (searchParams.get('site_id') || searchParams.get('site') || '').trim();
  const uiScope = ensureUiScope();
  const siteId = siteIdParam || uiScope.siteId || '';
  const isFilteredView = Boolean(cameraIdParam || deviceIdParam);
  const {
    byCamera: patternByCamera,
    error: patternContextError,
    refresh: refreshPatternContext,
  } = useLiveViewPatternContext(siteId, Boolean(siteId));
  const camerasByRowKey = useMemo(() => {
    const entries = cameras.map((camera) => [
      cameraRowKey(camera.camera_id, camera.edge_device_id),
      camera,
    ] as const);
    return new Map<string, Camera>(entries);
  }, [cameras]);

  const enabledCameras = useMemo(
    () => selectLiveViewGridCameras(cameras),
    [cameras],
  );

  const enabledCameraRowKeys = useMemo(
    () => enabledCameras.map((c) => cameraRowKey(c.camera_id, c.edge_device_id)),
    [enabledCameras],
  );

  useEffect(() => {
    const keys = enabledCameraRowKeys;
    if (keys.length === 0) {
      setTileRowOrder([]);
      return;
    }
    setTileRowOrder((prev) => {
      const saved = loadSavedTileOrder(siteId);
      const prevRelevant = prev.filter((k) => keys.includes(k));
      if (prev.length === 0 || prevRelevant.length === 0) {
        return mergeTileOrder(saved, keys);
      }
      return mergeTileOrder(prev, keys);
    });
  }, [enabledCameraRowKeys, siteId]);

  useEffect(() => {
    const keys = new Set(enabledCameraRowKeys);
    if (keys.size === 0) {
      setViewKindByCameraKey({});
      return;
    }
    setViewKindByCameraKey((prev) => {
      const saved = loadSavedViewKinds(siteId);
      const next: Record<string, ViewKind> = {};
      keys.forEach((key) => {
        const preferred = prev[key] || saved[key];
        if (preferred === 'annotated' || preferred === 'live') {
          next[key] = preferred;
        }
      });
      return next;
    });
  }, [enabledCameraRowKeys, siteId]);

  const orderedCandidateCameras = useMemo(() => {
    const map = new Map(
      enabledCameras.map((c) => [cameraRowKey(c.camera_id, c.edge_device_id), c]),
    );
    if (tileRowOrder.length === 0) return enabledCameras;
    const out: Camera[] = [];
    for (const k of tileRowOrder) {
      const c = map.get(k);
      if (c) out.push(c);
    }
    for (const c of enabledCameras) {
      const k = cameraRowKey(c.camera_id, c.edge_device_id);
      if (!tileRowOrder.includes(k)) out.push(c);
    }
    return out;
  }, [enabledCameras, tileRowOrder]);

  const urlPin = useMemo(
    () => ({ cameraId: cameraIdParam, deviceId: deviceIdParam }),
    [cameraIdParam, deviceIdParam],
  );

  const displayedCameras = useMemo(() => {
    return orderedCandidateCameras.filter((camera) => {
      const rowKey = cameraRowKey(camera.camera_id, camera.edge_device_id);
      const currentViewKind = resolvedViewKind(camera, viewKindByCameraKey[rowKey]);
      const streamKey = mjpegRowKey(camera.camera_id, camera.edge_device_id, currentViewKind);
      const stream = streams[streamKey];
      return shouldDisplayCameraInLiveView(camera, {
        showOffline: showOfflineCameras,
        pinToUrl: urlPin,
        hasPoster: Boolean(posterMap[rowKey]),
        hasStreamUrl: Boolean(stream?.url),
      });
    });
  }, [
    orderedCandidateCameras,
    posterMap,
    streams,
    viewKindByCameraKey,
    showOfflineCameras,
    urlPin,
  ]);

  const { offlineHiddenCount, waitingHiddenCount } = useMemo(() => {
    let offlineHidden = 0;
    let waitingHidden = 0;
    const displayedKeys = new Set(
      displayedCameras.map((c) => cameraRowKey(c.camera_id, c.edge_device_id)),
    );
    for (const camera of orderedCandidateCameras) {
      const rowKey = cameraRowKey(camera.camera_id, camera.edge_device_id);
      if (displayedKeys.has(rowKey)) continue;
      if (isCameraOfflineForDisplay(camera)) {
        offlineHidden += 1;
      } else {
        waitingHidden += 1;
      }
    }
    return { offlineHiddenCount: offlineHidden, waitingHiddenCount: waitingHidden };
  }, [displayedCameras, orderedCandidateCameras]);

  const poweredOffHiddenCount = useMemo(() => {
    const fromSummary = Number(bootstrapSummary?.cameras_on_offline_devices ?? 0);
    if (fromSummary > 0) return fromSummary;
    return offlineHiddenCount;
  }, [bootstrapSummary?.cameras_on_offline_devices, offlineHiddenCount]);

  const hiddenCandidateCount = poweredOffHiddenCount + waitingHiddenCount;

  const autoStartRowKeys = useMemo(() => {
    const next = new Set<string>();
    const bootstrapBudget = isFilteredView
      ? LIVE_VIEW_FILTERED_AUTO_START_BUDGET
      : LIVE_VIEW_AUTO_START_BUDGET;
    orderedCandidateCameras
      .filter((camera) => {
        const rowKey = cameraRowKey(camera.camera_id, camera.edge_device_id);
        return shouldDisplayCameraInLiveView(camera, {
          showOffline: false,
          pinToUrl: urlPin,
          hasPoster: Boolean(posterMap[rowKey]),
          hasStreamUrl: false,
        });
      })
      .slice(0, bootstrapBudget)
      .forEach((camera) => {
        next.add(cameraRowKey(camera.camera_id, camera.edge_device_id));
      });
    if (viewerStreamKey) {
      const { cameraId, deviceId } = parseMjpegStreamKey(viewerStreamKey);
      next.add(cameraRowKey(cameraId, deviceId));
    }
    return next;
  }, [isFilteredView, orderedCandidateCameras, posterMap, urlPin, viewerStreamKey]);

  useEffect(() => {
    autoStartRowKeysRef.current = autoStartRowKeys;
  }, [autoStartRowKeys]);

  useEffect(() => {
    viewerStreamKeyRef.current = viewerStreamKey;
  }, [viewerStreamKey]);

  useEffect(() => {
    const knownKeys = new Set(cameras.map((camera) => cameraRowKey(camera.camera_id, camera.edge_device_id)));
    setPosterMap((prev) => {
      const nextEntries = Object.entries(prev).filter(([key]) => knownKeys.has(key));
      if (nextEntries.length === Object.keys(prev).length) return prev;
      return Object.fromEntries(nextEntries);
    });
  }, [cameras]);

  const clearTileDragUi = () => {
    setTileDragKey(null);
    setTileDragOverKey(null);
  };

  const cancelReleaseTimer = useCallback((streamKey: string) => {
    const timerId = releaseTimersRef.current[streamKey];
    if (timerId) {
      window.clearTimeout(timerId);
      delete releaseTimersRef.current[streamKey];
    }
  }, []);

  useEffect(() => {
    const posterTargets = orderedCandidateCameras
      .filter((camera) => camera.enabled !== false && camera.status !== 'disabled')
      .filter((camera) => !posterMap[cameraRowKey(camera.camera_id, camera.edge_device_id)])
      .slice(0, isFilteredView ? 6 : Math.min(orderedCandidateCameras.length, 8));

    if (posterTargets.length === 0) return;

    scheduleDeferredWork(() => {
      void (async () => {
        for (const camera of posterTargets) {
          const rowKey = cameraRowKey(camera.camera_id, camera.edge_device_id);
          if (posterMap[rowKey] || posterLoadingKeysRef.current.has(rowKey)) continue;
          posterLoadingKeysRef.current.add(rowKey);
          try {
            const poster = await camerasApi.getCameraThumbnail(camera.camera_id, camera.edge_device_id);
            setPosterMap((prev) => (prev[rowKey] === poster ? prev : { ...prev, [rowKey]: poster }));
          } catch {
            // Best-effort only: a missing poster should not block the stream path.
          } finally {
            posterLoadingKeysRef.current.delete(rowKey);
          }
          await new Promise((resolve) => window.setTimeout(resolve, 120));
        }
      })();
    }, { timeoutMs: 1200 });
  }, [isFilteredView, orderedCandidateCameras, posterMap]);

  const handleTileDragStart = (rowKey: string) => (e: React.DragEvent) => {
    e.dataTransfer.setData('text/plain', rowKey);
    e.dataTransfer.effectAllowed = 'move';
    setTileDragKey(rowKey);
  };

  const handleTileDrop = (targetRowKey: string) => (e: React.DragEvent) => {
    e.preventDefault();
    const from = e.dataTransfer.getData('text/plain');
    if (!from || from === targetRowKey) {
      clearTileDragUi();
      return;
    }
    setTileRowOrder((prev) => {
      const keys = enabledCameraRowKeys;
      const base = prev.length > 0 ? mergeTileOrder(prev, keys) : [...keys];
      const next = reorderRowKeys(base, from, targetRowKey);
      persistTileOrder(siteId, next);
      return next;
    });
    clearTileDragUi();
  };

  useEffect(() => {
    // Auto-start streams for enabled cameras, but don't restart if already started.
    if (orderedCandidateCameras.length > 0) {
      orderedCandidateCameras.forEach((cam) => {
        if (cam.enabled === false || cam.status === 'disabled') return;
        const rowKey = cameraRowKey(cam.camera_id, cam.edge_device_id);
        if (!autoStartRowKeys.has(rowKey)) return;
        if (switchingCameraKeys.has(rowKey)) return;
        const currentViewKind = resolvedViewKind(cam, viewKindByCameraKey[rowKey]);
        if (getCameraUnavailableState(cam, currentViewKind)) return;
        if (usesAnnotatedEvidenceMode(currentViewKind)) return;
        const sk = mjpegRowKey(cam.camera_id, cam.edge_device_id, currentViewKind);
        if (startedStreamKeysRef.current.has(sk)) return;
        startedStreamKeysRef.current.add(sk);
        void startStream(
          cam.camera_id,
          cam.edge_device_id,
          preferredMjpegModeForCamera(cam.camera_id, currentViewKind),
          currentViewKind,
        );
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- startStream intentionally excluded
  }, [autoStartRowKeys, orderedCandidateCameras, viewKindByCameraKey, switchingCameraKeys]);

  useEffect(() => {
    const ref = startedStreamKeysRef;
    // Cleanup: stop all started streams on unmount only.
    return () => {
      const started = Array.from(ref.current.values());
      started.forEach((sk) => {
        const { cameraId, deviceId, viewKind } = parseMjpegStreamKey(sk);
        void Promise.resolve(mjpegStopStream(cameraId, deviceId, viewKind)).catch(() => {});
      });
      // Clear retry timers
      Object.values(retryStateRef.current).forEach((s) => {
        if (s?.timerId) window.clearTimeout(s.timerId);
      });
      Object.values(releaseTimersRef.current).forEach((timerId) => {
        if (timerId) window.clearTimeout(timerId);
      });
      retryStateRef.current = {};
      releaseTimersRef.current = {};
      ref.current.clear();
    };
  }, []);

  const fetchCameras = async (opts?: { background?: boolean }) => {
    const background = opts?.background === true;
    if (!siteId) {
      setCameras([]);
      setError('Org/site scope is not set yet. Re-login or choose scope from the header.');
      if (!background) setLoading(false);
      return;
    }
    try {
      if (!background) setLoading(true);
      if (!background) setError(null);
      const response = await camerasApi.listCameraBootstrap(siteId, deviceIdParam);
      let items: Camera[] = Array.isArray(response?.items) ? response.items : [];
      // Do NOT hard-filter by camera_id query param.
      // camera_id is used as a focus hint (scroll target), not as an exclusion filter,
      // so operators always see all available cameras.
      // Warehouse mode should not show OCR cameras at all.
      if (mode === 'warehouse') {
        items = items.filter((c) => {
          const po = String((c as any)?.pipeline_override || '').toLowerCase();
          const p = String((c as any)?.pipeline || '').toLowerCase();
          return !(po.includes('ocr') || p.includes('ocr'));
        });
      }
      if (cameraIdParam) {
        items = [...items].sort((a, b) => {
          if (a.camera_id === cameraIdParam && b.camera_id !== cameraIdParam) return -1;
          if (b.camera_id === cameraIdParam && a.camera_id !== cameraIdParam) return 1;
          return 0;
        });
      }
      setCameras(items);
      setBootstrapSummary(response?.summary ?? null);
      
      // If camera_id param is provided, scroll to the first matching tile (device-aware id).
      if (cameraIdParam) {
        const match = items.find((c) => c.camera_id === cameraIdParam);
        if (match) {
          const rowKey = cameraRowKey(match.camera_id, match.edge_device_id);
          const domId = `camera-${rowKey.replace(/[^a-zA-Z0-9_-]/g, '_')}`;
          setTimeout(() => {
            const element = document.getElementById(domId);
            if (element) element.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }, 500);
        }
      }
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || 'Failed to load cameras');
    } finally {
      if (!background) setLoading(false);
    }
  };

  useEffect(() => {
    void fetchCameras();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteId, deviceIdParam, cameraIdParam, mode]);

  useEffect(() => {
    if (!loading) {
      setBootstrapSlow(false);
      return undefined;
    }
    const timerId = window.setTimeout(() => setBootstrapSlow(true), 8000);
    return () => window.clearTimeout(timerId);
  }, [loading]);

  useEffect(() => {
    const poll = () => {
      if (document.visibilityState !== 'visible') return;
      void fetchCameras({ background: true });
    };

    const intervalId = window.setInterval(poll, 15000);
    const onFocus = () => poll();
    const onVisibility = () => {
      if (!document.hidden) poll();
    };

    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.clearInterval(intervalId);
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisibility);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteId, deviceIdParam, cameraIdParam, mode]);

  const setStopped = (streamKey: string, stopped: boolean) => {
    if (stopped) stoppedStreamKeysRef.current.add(streamKey);
    else stoppedStreamKeysRef.current.delete(streamKey);
    setStoppedStreamKeys(new Set(stoppedStreamKeysRef.current));
  };

  useEffect(() => {
    orderedCandidateCameras.forEach((cam) => {
      if (cam.enabled === false || cam.status === 'disabled') return;
      const rowKey = cameraRowKey(cam.camera_id, cam.edge_device_id);
      if (autoStartRowKeys.has(rowKey)) return;
      const currentViewKind = resolvedViewKind(cam, viewKindByCameraKey[rowKey]);
      if (usesAnnotatedEvidenceMode(currentViewKind)) return;
      const sk = mjpegRowKey(cam.camera_id, cam.edge_device_id, currentViewKind);
      if (startedStreamKeysRef.current.has(sk) || stoppedStreamKeysRef.current.has(sk)) return;
      setStopped(sk, true);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- setStopped is stable; refs track stream lifecycle
  }, [autoStartRowKeys, orderedCandidateCameras, viewKindByCameraKey]);

  const startStream = async (
    cameraId: string,
    deviceId?: string,
    mode?: 'stable' | 'low_latency',
    viewKind: ViewKind = 'live',
    opts?: { allowAnnotatedRecovery?: boolean },
  ): Promise<boolean> => {
    const sk = mjpegRowKey(cameraId, deviceId, viewKind);
    if (usesAnnotatedEvidenceMode(viewKind)) {
      clearRetry(sk);
      setStreams((prev) => ({ ...prev, [sk]: { url: null, loading: false, error: null } }));
      return true;
    }
    const allowAnnotatedRecovery = opts?.allowAnnotatedRecovery !== false;
    const camera = camerasByRowKey.get(cameraRowKey(cameraId, deviceId));
    if (camera && getCameraUnavailableState(camera, viewKind)) {
      startedStreamKeysRef.current.delete(sk);
      clearRetry(sk);
      setStreams((prev) => ({ ...prev, [sk]: { url: null, loading: false, error: null } }));
      return false;
    }
    // If user explicitly stopped this stream, don't auto-start it.
    if (stoppedStreamKeysRef.current.has(sk)) return false;
    const nonce = Date.now();
    startNonceRef.current[sk] = nonce;
    setStreams((prev) => ({ ...prev, [sk]: { url: null, loading: true, error: null } }));
    try {
      const streamStatus = await mjpegStartStream(
        cameraId,
        undefined,
        deviceId,
        mode || preferredMjpegModeForCamera(cameraId, viewKind),
        viewKind,
      );
      if (streamStatus.ok && streamStatus.mjpeg_url) {
        const baseUrl = window.location.origin;
        const sep = streamStatus.mjpeg_url.includes('?') ? '&' : '?';
        const cacheBuster = `_=${Date.now()}`;
        const mjpegUrl = `${baseUrl}${streamStatus.mjpeg_url}${sep}${cacheBuster}`;

        const waitForRealSnapshot = async (): Promise<boolean> => {
          const deadlineMs =
            Date.now()
            + (viewKind === 'annotated'
              ? (allowAnnotatedRecovery ? ANNOTATED_INITIAL_RECOVERY_WAIT_MS : ANNOTATED_POST_RECOVERY_WAIT_MS)
              : cameraId.startsWith('cam_')
                ? 30_000
                : 60_000);
          let realSnapshotStreak = 0;
          const requiredSnapshotStreak =
            viewKind === 'live' ? MJPEG_LIVE_READY_SNAPSHOT_STREAK : 1;
          while (Date.now() < deadlineMs) {
            if (startNonceRef.current[sk] !== nonce) return false;
            try {
              const snapshot = await mjpegGetSnapshot(cameraId, deviceId, viewKind);
              const snapshotReady =
                viewKind === 'live'
                  ? isLikelyRealMjpegSnapshot(snapshot)
                  : snapshot.isRealFrame;
              if (snapshotReady) {
                realSnapshotStreak += 1;
                if (realSnapshotStreak >= requiredSnapshotStreak) {
                  return true;
                }
              } else {
                realSnapshotStreak = 0;
              }
            } catch {
              realSnapshotStreak = 0;
            }
            await new Promise((r) => setTimeout(r, viewKind === 'live' ? 350 : 750));
          }
          return false;
        };

        const handleSnapshotWaitFailure = async (): Promise<boolean> => {
          if (
            viewKind === 'annotated'
            && allowAnnotatedRecovery
            && !readOnlyEvaluationFeed
            && usesAnnotatedLiveStream(viewKind)
          ) {
            const recovered = await recoverAnnotatedCamera(cameraId, deviceId, annotatedRecoveryRef);
            if (recovered) {
              return startStream(
                cameraId,
                deviceId,
                mode || preferredMjpegModeForCamera(cameraId, viewKind),
                viewKind,
                { allowAnnotatedRecovery: false },
              );
            }
          }
          const annotatedReadOnlyMsg =
            'Annotated view is still starting. Read-only accounts cannot restart the edge pipeline; retrying automatically.';
          setStreams((prev) => ({
            ...prev,
            [sk]: {
              url: null,
              loading: false,
              error:
                viewKind === 'annotated' && readOnlyEvaluationFeed
                  ? annotatedReadOnlyMsg
                  : 'Stream is taking too long to produce first frame (will retry)',
            },
          }));
          scheduleRetry(
            sk,
            cameraId,
            deviceId,
            'No first frame yet',
            mode || preferredMjpegModeForCamera(cameraId, viewKind),
            viewKind,
          );
          return false;
        };

        // Live View: keep the poster visible until the first real MJPEG frame is
        // confirmed so placeholder JPEGs do not flash as grey boxes on RTSP feeds.
        if (viewKind === 'live') {
          void (async () => {
            const ready = await waitForRealSnapshot();
            if (startNonceRef.current[sk] !== nonce) return;
            if (stoppedStreamKeysRef.current.has(sk)) return;
            if (!ready) {
              void handleSnapshotWaitFailure();
              return;
            }
            setStreams((prev) => ({
              ...prev,
              [sk]: { url: mjpegUrl, loading: false, error: null },
            }));
          })();
          return true;
        }

        const ready = await waitForRealSnapshot();
        if (!ready) {
          return handleSnapshotWaitFailure();
        }

        setStreams((prev) => ({
          ...prev,
          [sk]: { url: mjpegUrl, loading: false, error: null },
        }));
        return true;
      } else {
        setStreams((prev) => ({
          ...prev,
          [sk]: { url: null, loading: false, error: streamStatus.error || 'Failed to start stream' },
        }));
        return false;
      }
    } catch (err: any) {
      setStreams((prev) => ({
        ...prev,
        [sk]: { url: null, loading: false, error: err?.message || 'Failed to start stream' },
      }));
      return false;
    }
  };

  const startLiveStreamForCamera = (
    camera: Camera,
    viewKind: ViewKind = 'live',
  ) => {
    const sk = mjpegRowKey(camera.camera_id, camera.edge_device_id, viewKind);
    if (getCameraUnavailableState(camera, viewKind)) return;
    if (usesAnnotatedEvidenceMode(viewKind)) return;
    if (startedStreamKeysRef.current.has(sk) && streams[sk]?.url) return;
    setStopped(sk, false);
    startedStreamKeysRef.current.add(sk);
    manualHoldKeysRef.current.add(sk);
    cancelReleaseTimer(sk);
    void startStream(
      camera.camera_id,
      camera.edge_device_id,
      preferredMjpegModeForCamera(camera.camera_id, viewKind),
      viewKind,
    );
  };

  const scheduleRetry = (
    streamKey: string,
    cameraId: string,
    deviceId?: string,
    reason?: string,
    mode?: 'stable' | 'low_latency',
    viewKind: ViewKind = 'live',
  ) => {
    if (stoppedStreamKeysRef.current.has(streamKey)) return;
    const camera = camerasByRowKey.get(cameraRowKey(cameraId, deviceId));
    if (camera && getCameraUnavailableState(camera, viewKind)) {
      clearRetry(streamKey);
      setStreams((prev) => ({
        ...prev,
        [streamKey]: { url: null, loading: false, error: null },
      }));
      return;
    }

    const existing = retryStateRef.current[streamKey] || { attempt: 0, timerId: null };
    const attempt = Math.min(10, (existing.attempt || 0) + 1);
    const delayMs = Math.min(30_000, 1000 * Math.pow(2, Math.max(0, attempt - 1)));

    if (existing.timerId) window.clearTimeout(existing.timerId);
    const timerId = window.setTimeout(() => {
      void startStream(
        cameraId,
        deviceId,
        mode || preferredMjpegModeForCamera(cameraId, viewKind),
        viewKind,
      );
    }, delayMs);

    retryStateRef.current[streamKey] = { attempt, timerId };
    setStreams((prev) => ({
      ...prev,
      [streamKey]: {
        url: prev[streamKey]?.url || null,
        loading: false,
        error: reason ? `${reason} (retrying in ${Math.round(delayMs / 1000)}s)` : `Stream disconnected (retrying in ${Math.round(delayMs / 1000)}s)`,
      },
    }));
  };

  const clearRetry = useCallback((streamKey: string) => {
    const s = retryStateRef.current[streamKey];
    if (s?.timerId) window.clearTimeout(s.timerId);
    retryStateRef.current[streamKey] = { attempt: 0, timerId: null };
  }, []);

  const stopStream = async (cameraId: string, deviceId?: string, viewKind: ViewKind = 'live') => {
    const sk = mjpegRowKey(cameraId, deviceId, viewKind);
    if (usesAnnotatedEvidenceMode(viewKind)) {
      setStopped(sk, true);
      manualHoldKeysRef.current.delete(sk);
      clearRetry(sk);
      setStreams((prev) => ({ ...prev, [sk]: { url: null, loading: false, error: null } }));
      return;
    }
    try {
      setStopped(sk, true);
      manualHoldKeysRef.current.delete(sk);
      await mjpegStopStream(cameraId, deviceId, viewKind);
      delete startNonceRef.current[sk];
      startedStreamKeysRef.current.delete(sk);
      setStreams((prev) => ({ ...prev, [sk]: { url: null, loading: false, error: null } }));
    } catch (err) {
      console.error('Failed to stop stream:', err);
    }
  };

  const releaseStream = useCallback(async (cameraId: string, deviceId?: string, viewKind: ViewKind = 'live') => {
    const sk = mjpegRowKey(cameraId, deviceId, viewKind);
    const rowKey = cameraRowKey(cameraId, deviceId);
    try {
      cancelReleaseTimer(sk);
      clearRetry(sk);
      if (!usesAnnotatedEvidenceMode(viewKind)) {
        await mjpegStopStream(cameraId, deviceId, viewKind);
      }
    } catch (err) {
      console.debug('Failed to release stream:', err);
    } finally {
      delete startNonceRef.current[sk];
      startedStreamKeysRef.current.delete(sk);
      manualHoldKeysRef.current.delete(sk);
      // Manual tiles must return to an explicit stopped idle so Start Live View
      // is enabled again (Start is gated on isStopped).
      if (!autoStartRowKeysRef.current.has(rowKey)) {
        stoppedStreamKeysRef.current.add(sk);
      } else {
        stoppedStreamKeysRef.current.delete(sk);
      }
      setStoppedStreamKeys(new Set(stoppedStreamKeysRef.current));
      setStreams((prev) => ({ ...prev, [sk]: { url: null, loading: false, error: null } }));
    }
  }, [cancelReleaseTimer, clearRetry]);

  useEffect(() => {
    const started = Array.from(startedStreamKeysRef.current.values());
    started.forEach((streamKey) => {
      const { cameraId, deviceId, viewKind } = parseMjpegStreamKey(streamKey);
      const rowKey = cameraRowKey(cameraId, deviceId);
      const shouldKeepHot =
        autoStartRowKeys.has(rowKey)
        || viewerStreamKey === streamKey
        || stoppedStreamKeysRef.current.has(streamKey)
        || manualHoldKeysRef.current.has(streamKey);

      if (shouldKeepHot) {
        cancelReleaseTimer(streamKey);
        return;
      }
      if (releaseTimersRef.current[streamKey]) return;

      releaseTimersRef.current[streamKey] = window.setTimeout(() => {
        delete releaseTimersRef.current[streamKey];
        const keepNow =
          autoStartRowKeysRef.current.has(rowKey)
          || viewerStreamKeyRef.current === streamKey
          || stoppedStreamKeysRef.current.has(streamKey)
          || manualHoldKeysRef.current.has(streamKey);
        if (keepNow) return;
        void releaseStream(cameraId, deviceId, viewKind);
      }, OFFSCREEN_RELEASE_GRACE_MS);
    });
  }, [autoStartRowKeys, cancelReleaseTimer, releaseStream, viewerStreamKey]);

  const handleRefresh = () => {
    void fetchCameras();
  };

  const switchCameraViewKind = async (camera: Camera, nextViewKind: ViewKind) => {
    const rowKey = cameraRowKey(camera.camera_id, camera.edge_device_id);
    const currentViewKind = resolvedViewKind(camera, viewKindByCameraKey[rowKey]);
    const requestedViewKind = resolvedViewKind(camera, nextViewKind);
    if (switchingCameraKeys.has(rowKey)) return;
    if (currentViewKind === requestedViewKind) return;

    const previousStreamKey = mjpegRowKey(camera.camera_id, camera.edge_device_id, currentViewKind);
    const nextStreamKey = mjpegRowKey(camera.camera_id, camera.edge_device_id, requestedViewKind);

    setSwitchingCameraKeys((prev) => {
      const updated = new Set(prev);
      updated.add(rowKey);
      return updated;
    });
    stoppedStreamKeysRef.current.delete(nextStreamKey);
    setStoppedStreamKeys(new Set(stoppedStreamKeysRef.current));
    setStreams((prev) => {
      const updated = { ...prev };
      if (!updated[nextStreamKey]) {
        updated[nextStreamKey] = { url: null, loading: true, error: null };
      }
      return updated;
    });

    try {
      let switched = false;
      const unavailableState = getCameraUnavailableState(camera, requestedViewKind);
      if (unavailableState) {
        setStreams((prev) => ({
          ...prev,
          [nextStreamKey]: { url: null, loading: false, error: null },
        }));
        switched = false;
      } else if (usesAnnotatedEvidenceMode(requestedViewKind)) {
        setStreams((prev) => ({
          ...prev,
          [nextStreamKey]: { url: null, loading: false, error: null },
        }));
        switched = true;
      } else if (!startedStreamKeysRef.current.has(nextStreamKey)) {
        startedStreamKeysRef.current.add(nextStreamKey);
        switched = await startStream(
          camera.camera_id,
          camera.edge_device_id,
          preferredMjpegModeForCamera(camera.camera_id, requestedViewKind),
          requestedViewKind,
        );
      } else {
        setStreams((prev) => {
          const existing = prev[nextStreamKey];
          if (!existing) return prev;
          const returningToLive =
            requestedViewKind === 'live' && currentViewKind !== 'live';
          return {
            ...prev,
            [nextStreamKey]: {
              ...existing,
              url: returningToLive
                ? (existing.url ? withFreshCacheBuster(existing.url) : existing.url)
                : requestedViewKind === 'live'
                  ? existing.url
                  : withFreshCacheBuster(existing.url || ''),
              loading: false,
              error: null,
            },
          };
        });
        switched = true;
      }
      if (switched) {
        setViewKindByCameraKey((prev) => {
          const next = { ...prev, [rowKey]: requestedViewKind };
          persistViewKinds(siteId, next);
          return next;
        });
        if (usesAnnotatedEvidenceMode(requestedViewKind)) {
          // Annotated Evidence is a read-only panel; keep the live MJPEG session warm so
          // returning to Live does not cold-restart ffmpeg and replay RTSP backlog.
          cancelReleaseTimer(mjpegRowKey(camera.camera_id, camera.edge_device_id, 'live'));
        } else {
          await releaseStream(camera.camera_id, camera.edge_device_id, currentViewKind);
        }
        if (viewerStreamKey === previousStreamKey) {
          setViewerStreamKey(nextStreamKey);
          setViewerTitle(formatLiveViewDialogTitle(camera.name || camera.camera_id, requestedViewKind));
        }
      }
    } finally {
      setSwitchingCameraKeys((prev) => {
        const updated = new Set(prev);
        updated.delete(rowKey);
        return updated;
      });
    }
  };

  const handleOpenViewerFullscreen = (camera: Camera) => {
    setViewerFullScreen(true);
    openViewer(camera);
  };

  const openViewer = (camera: Camera) => {
    const currentViewKind = resolvedViewKind(
      camera,
      viewKindByCameraKey[cameraRowKey(camera.camera_id, camera.edge_device_id)]
    );
    const streamKey = mjpegRowKey(camera.camera_id, camera.edge_device_id, currentViewKind);
    setViewerStreamKey(streamKey);
    setViewerTitle(formatLiveViewDialogTitle(camera.name || camera.camera_id, currentViewKind));
    setZoom(1);
    setPan({ x: 0, y: 0 });
    setViewerOpen(true);
    if (usesAnnotatedEvidenceMode(currentViewKind)) {
      return;
    }
    if (
      !startedStreamKeysRef.current.has(streamKey)
      || stoppedStreamKeysRef.current.has(streamKey)
    ) {
      if (!getCameraUnavailableState(camera, currentViewKind)) {
        startLiveStreamForCamera(camera, currentViewKind);
      }
    } else if (currentViewKind !== 'live') {
      refreshStreamConnection(streamKey);
    }
  };

  const closeViewer = () => {
    setViewerOpen(false);
    setViewerStreamKey(null);
    setViewerFullScreen(false);
  };

  const viewerStream = useMemo(() => {
    if (!viewerStreamKey) return null;
    return streams[viewerStreamKey] || null;
  }, [streams, viewerStreamKey]);

  const viewerCamera = useMemo(() => {
    if (!viewerStreamKey) return null;
    const { cameraId, deviceId } = parseMjpegStreamKey(viewerStreamKey);
    return cameras.find(
      (c) => c.camera_id === cameraId && (deviceId ? c.edge_device_id === deviceId : true)
    ) || null;
  }, [cameras, viewerStreamKey]);

  const viewerViewKind: ViewKind = useMemo(() => {
    if (!viewerStreamKey) return 'live';
    return parseMjpegStreamKey(viewerStreamKey).viewKind;
  }, [viewerStreamKey]);
  const viewerUnavailableState = useMemo(() => {
    if (!viewerCamera) return null;
    return getCameraUnavailableState(viewerCamera, viewerViewKind);
  }, [viewerCamera, viewerViewKind]);

  const refreshStreamConnection = (streamKey: string) => {
    setStreams((prev) => {
      const existing = prev[streamKey];
      if (!existing?.url || existing.loading || existing.error) return prev;
      return {
        ...prev,
        [streamKey]: {
          ...existing,
          url: withFreshCacheBuster(existing.url),
        },
      };
    });
  };

  const onViewerWheel: React.WheelEventHandler<HTMLDivElement> = (e) => {
    e.preventDefault();
    const delta = e.deltaY;
    setZoom((z) => {
      const next = delta > 0 ? z * 0.9 : z * 1.1;
      return Math.min(4, Math.max(1, next));
    });
  };

  const onViewerPointerDown: React.PointerEventHandler<HTMLDivElement> = (e) => {
    if (zoom <= 1) return;
    (e.currentTarget as any).setPointerCapture?.(e.pointerId);
    draggingRef.current = {
      dragging: true,
      startX: e.clientX,
      startY: e.clientY,
      baseX: pan.x,
      baseY: pan.y,
    };
  };

  const onViewerPointerMove: React.PointerEventHandler<HTMLDivElement> = (e) => {
    if (!draggingRef.current.dragging) return;
    const dx = e.clientX - draggingRef.current.startX;
    const dy = e.clientY - draggingRef.current.startY;
    setPan({ x: draggingRef.current.baseX + dx, y: draggingRef.current.baseY + dy });
  };

  const onViewerPointerUp: React.PointerEventHandler<HTMLDivElement> = () => {
    draggingRef.current.dragging = false;
  };

  if (error && !cameras.length) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        {error}
      </Alert>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
        <Box>
          <Typography variant="h4" gutterBottom>
            {LIVE_VIEW_PAGE_TITLE}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {LIVE_VIEW_PAGE_SUBTITLE}
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.75 }} data-testid="live-view-help">
            {LIVE_VIEW_HELP_LINE}
          </Typography>
        </Box>
        <RefreshIconButton onClick={handleRefresh} title="Refresh camera list" />
      </Box>

      {sandboxEvaluationFeed ? (
        <Alert severity="warning" sx={{ mb: 2 }} data-testid="live-view-sandbox-banner">
          Controlled evaluation sandbox. No production employer data. You can review the feed, zone, policy, Safety Cases, and corrective actions on demo/ws1 only.
        </Alert>
      ) : readOnlyEvaluationFeed ? (
        <Alert severity="info" sx={{ mb: 2 }} data-testid="live-view-evaluation-readonly-banner">
          Evaluation feed — read-only. Live View and Annotated Evidence are available for review; setup and configuration changes are not available from this account.
        </Alert>
      ) : null}

      {isFilteredView && (
        <Alert
          severity="info"
          sx={{ mb: 2 }}
          action={(
            <Button
              color="inherit"
              size="small"
              onClick={() => {
                const q = new URLSearchParams(searchParams.toString());
                q.delete('camera_id');
                q.delete('device_id');
                navigate(`/live-view${q.toString() ? `?${q.toString()}` : ''}`, { replace: true });
              }}
            >
              Clear Filters
            </Button>
          )}
        >
          Live View opened with URL filters/hints
          {showTechnicalIds && cameraIdParam ? ` (camera: ${cameraIdParam})` : ''}
          {showTechnicalIds && deviceIdParam ? ` (device: ${deviceIdParam})` : ''}.
        </Alert>
      )}

      {poweredOffHiddenCount > 0 || waitingHiddenCount > 0 ? (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1, mb: 1.5 }}>
          {poweredOffHiddenCount > 0 ? (
            <Typography
              variant="body2"
              color="text.secondary"
              data-testid="live-view-offline-hidden-count"
              title="Incidents may still be captured when powered-off units return online."
            >
              {formatPoweredOffHiddenLabel(poweredOffHiddenCount)}
            </Typography>
          ) : null}
          {waitingHiddenCount > 0 ? (
            <Typography variant="body2" color="text.secondary">
              {waitingHiddenCount.toLocaleString()} camera{waitingHiddenCount === 1 ? '' : 's'} waiting for a poster or stream
            </Typography>
          ) : null}
          {poweredOffHiddenCount > 0 ? (
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  checked={showOfflineCameras}
                  onChange={(_, checked) => setShowOfflineCameras(checked)}
                  inputProps={{ 'aria-label': 'Show offline cameras' }}
                />
              }
              label={
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  Show offline cameras
                </Typography>
              }
              sx={{ ml: 0 }}
            />
          ) : null}
        </Box>
      ) : null}

      {loading && displayedCameras.length === 0 ? (
        <>
          {bootstrapSlow ? (
            <Alert severity="info" sx={{ mb: 2 }}>
              Loading cameras from the cloud (often 10–15s while edge runtime status is collected).
              If this page stays blank, try again or check Diagnostics.
            </Alert>
          ) : null}
          <Grid container spacing={2}>
          {[0, 1, 2, 3].map((i) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={i}>
              <Card>
                <Skeleton variant="rectangular" height={220} animation="wave" />
                <CardContent>
                  <Skeleton variant="text" width="50%" />
                  <Skeleton variant="text" width="30%" />
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
        </>
      ) : displayedCameras.length === 0 ? (
        <Alert severity="info">
          {poweredOffHiddenCount > 0 && !showOfflineCameras
            ? `${poweredOffHiddenCount.toLocaleString()} enabled camera${poweredOffHiddenCount === 1 ? ' is' : 's are'} on powered-off units and hidden. Turn on Show offline cameras to review safety conditions without a live feed.`
            : hiddenCandidateCount > 0
              ? `Waiting for live cameras. ${waitingHiddenCount.toLocaleString()} enabled camera${waitingHiddenCount === 1 ? '' : 's'} will appear here when a poster or live stream is available.`
              : 'No live cameras found. Enable cameras in the Cameras page to view Live or Annotated outputs.'}
        </Alert>
      ) : (
        <Box
          className={LIVE_VIEW_CAMERA_GRID_FILL_CLASS}
          data-testid="live-view-camera-grid"
          sx={{ width: { xs: '100%', md: '82%' }, maxWidth: '100%' }}
        >
        <Grid container spacing={2} sx={{ width: '100%' }}>
          {displayedCameras.map((camera) => {
            const rowKey = cameraRowKey(camera.camera_id, camera.edge_device_id);
            const currentViewKind = resolvedViewKind(camera, viewKindByCameraKey[rowKey]);
            const annotatedAvailable = cameraSupportsAnnotated(camera);
            const evidenceMode = usesAnnotatedEvidenceMode(currentViewKind);
            const isSwitchingView = switchingCameraKeys.has(rowKey);
            const shouldAutoStart = autoStartRowKeys.has(rowKey);
            const sk = mjpegRowKey(camera.camera_id, camera.edge_device_id, currentViewKind);
            const stream = streams[sk];
            const poster = posterMap[rowKey];
            const unavailableState = getCameraUnavailableState(camera, currentViewKind);
            const isRunning = !evidenceMode && Boolean(stream?.url) && !stream?.loading && !stream?.error && !unavailableState;
            const isTileDragOver = Boolean(tileDragKey && tileDragOverKey === rowKey && tileDragKey !== rowKey);
            const patternLink = lookupLiveViewCameraPatternLink(patternByCamera, camera.camera_id);
            const cameraTitle = camera.name || (showTechnicalIds ? camera.camera_id : 'Camera');
            const statusLine = liveViewCameraStatusLine(
              cameraEvidenceStatusMeta(camera).label,
              isRunning,
            );
            return (
              <Grid
                item
                xs={12}
                md={6}
                lg={6}
                key={rowKey}
                ref={(node: HTMLDivElement | null) => {
                  tileViewportNodesRef.current[rowKey] = node;
                }}
                id={`camera-${rowKey.replace(/[^a-zA-Z0-9_-]/g, '_')}`}
                data-testid="live-view-camera-tile"
                data-camera-id={camera.camera_id}
                onDragOver={(e) => {
                  e.preventDefault();
                  e.dataTransfer.dropEffect = 'move';
                  if (tileDragKey && tileDragKey !== rowKey) setTileDragOverKey(rowKey);
                }}
                onDrop={handleTileDrop(rowKey)}
                sx={{
                  transition: 'outline-offset 0.15s ease',
                  outline: isTileDragOver ? '2px dashed' : 'none',
                  outlineColor: 'primary.main',
                  outlineOffset: 4,
                  borderRadius: 1,
                }}
              >
                <Card
                  sx={{
                    width: '100%',
                    height: '100%',
                    opacity: tileDragKey === rowKey ? 0.55 : 1,
                    transition: 'opacity 0.15s ease',
                  }}
                >
                  <CardContent>
                    <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5, minWidth: 0, flex: 1, pr: 1 }}>
                        <Tooltip title="Drag to reorder tiles">
                          <IconButton
                            size="small"
                            aria-label="Reorder camera tile"
                            draggable
                            onDragStart={handleTileDragStart(rowKey)}
                            onDragEnd={clearTileDragUi}
                            sx={{ cursor: 'grab', flexShrink: 0, '&:active': { cursor: 'grabbing' } }}
                          >
                            <DragIndicatorIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography
                          variant="h6"
                          data-testid="live-view-camera-title"
                          sx={{ lineHeight: 1.25, overflowWrap: 'anywhere', whiteSpace: 'normal' }}
                        >
                          {cameraTitle}
                        </Typography>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          display="block"
                          noWrap
                          data-testid="live-view-camera-status"
                        >
                          {statusLine}
                        </Typography>
                        {showTechnicalIds && camera.edge_device_id ? (
                          <Typography variant="caption" color="text.secondary" display="block" noWrap>
                            Device: {camera.edge_device_id}
                          </Typography>
                        ) : null}
                        {showTechnicalIds ? (
                          <Typography variant="caption" color="text.secondary" display="block" noWrap>
                            {formatCameraLastFrameLabel(
                              cameraLastFrameTimestamp(camera),
                              { offline: isCameraOfflineForDisplay(camera) },
                            )}
                          </Typography>
                        ) : null}
                        </Box>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
                        {isRunning ? (
                          <Tooltip title={LIVE_VIEW_STOP_LABEL}>
                            <IconButton
                              size="small"
                              aria-label={LIVE_VIEW_STOP_LABEL}
                              data-testid="live-view-stop-live"
                              onClick={() => stopStream(camera.camera_id, camera.edge_device_id, currentViewKind)}
                            >
                              <StopIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        ) : null}
                        <Tooltip title="Open large view">
                          <IconButton size="small" onClick={() => openViewer(camera)}>
                            <ZoomInIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Open fullscreen">
                          <IconButton size="small" onClick={() => handleOpenViewerFullscreen(camera)}>
                            <FullscreenIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
                      <ToggleButtonGroup
                        size="small"
                        exclusive
                        value={currentViewKind}
                        disabled={isSwitchingView}
                        sx={{
                          '& .MuiToggleButton-root.Mui-selected': {
                            color: 'common.white',
                            backgroundColor: 'primary.main',
                            borderColor: 'primary.main',
                          },
                          '& .MuiToggleButton-root.Mui-selected:hover': {
                            backgroundColor: 'primary.dark',
                            borderColor: 'primary.dark',
                          },
                        }}
                        onChange={(_, nextValue: ViewKind | null) => {
                          if (!nextValue) return;
                          if (
                            nextValue === 'live'
                            && !shouldAutoStart
                            && !evidenceMode
                            && !isRunning
                            && !stream?.loading
                            && !unavailableState
                          ) {
                            startLiveStreamForCamera(camera, 'live');
                          }
                          void switchCameraViewKind(camera, nextValue);
                        }}
                        aria-label={`View selector for ${cameraTitle}`}
                      >
                        <ToggleButton value="live">Live View</ToggleButton>
                        <ToggleButton value="annotated" disabled={!annotatedAvailable}>
                          Annotated Evidence
                        </ToggleButton>
                      </ToggleButtonGroup>
                    </Box>
                    <Box
                      sx={{
                        position: 'relative',
                        width: '100%',
                        // Larger tiles: slightly taller viewport while preserving aspect.
                        paddingTop: '60%', // ~16:9-ish but taller for better visibility
                        backgroundColor: unavailableState ? '#f3f4f6' : '#000',
                        borderRadius: 1,
                        overflow: 'hidden',
                        mt: 1,
                        cursor: isRunning || (!isRunning && !stream?.loading && !evidenceMode && !unavailableState)
                          ? 'pointer'
                          : 'default',
                      }}
                      data-testid="live-view-media-frame"
                      onClick={() => {
                        if (evidenceMode) return;
                        if (isRunning) {
                          openViewer(camera);
                          return;
                        }
                        if (!stream?.loading && !unavailableState) {
                          startLiveStreamForCamera(camera, currentViewKind);
                        }
                      }}
                    >
                      {evidenceMode && !unavailableState && (
                        <AnnotatedEvidencePanel
                          cameraId={camera.camera_id}
                          edgeDeviceId={camera.edge_device_id}
                          cameraName={camera.name || (showTechnicalIds ? camera.camera_id : 'Camera')}
                          active={shouldAutoStart || evidenceMode}
                          showTechnicalIds={showTechnicalIds}
                        />
                      )}
                      {/* Poster underlay only while starting. Once the MJPEG URL is live,
                          keep it alone — a 1x1 grey placeholder with object-fit:contain
                          over a poster underlay looks like a grey center box with video edges. */}
                      {!evidenceMode && stream?.url && stream?.loading && poster && !unavailableState && (
                        <img
                          src={poster}
                          alt={`${cameraTitle} poster underlay`}
                          style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: '100%',
                            objectFit: 'contain',
                            zIndex: 0,
                          }}
                        />
                      )}
                      {!evidenceMode && !stream?.url && poster && !unavailableState && (
                        <ContainedMediaOverlay
                          src={poster}
                          alt={`${cameraTitle} poster`}
                        />
                      )}
                      {unavailableState && (
                        <Box
                          sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            p: 2,
                            background:
                              'linear-gradient(180deg, rgba(255,255,255,0.98) 0%, rgba(245,247,250,0.98) 100%)',
                          }}
                        >
                          <Alert
                            severity="info"
                            sx={{ width: '100%', maxWidth: 420, backgroundColor: 'rgba(255,255,255,0.96)' }}
                          >
                            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                              {unavailableState.title}
                            </Typography>
                            <Typography variant="body2">{unavailableState.message}</Typography>
                            {unavailableState.detail && (
                              <Typography variant="caption" display="block" sx={{ mt: 0.75 }}>
                                {unavailableState.detail}
                              </Typography>
                            )}
                          </Alert>
                        </Box>
                      )}
                      {!evidenceMode && stream?.loading && !unavailableState && (
                        <Box
                          sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                          }}
                        >
                          <CircularProgress size={40} />
                        </Box>
                      )}
                      {!evidenceMode && stream?.error && !unavailableState && (
                        <Box
                          sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            p: 2,
                          }}
                        >
                          <Alert severity="warning" sx={{ width: '100%' }}>
                            {stream.error}
                          </Alert>
                        </Box>
                      )}
                      {!evidenceMode && stream?.url && !stream.loading && !stream.error && !unavailableState && (
                        <ContainedMediaOverlay
                          src={stream.url}
                          alt={cameraTitle}
                          imageStyle={{
                            zIndex: 1,
                          }}
                          onLoad={() => {
                            clearRetry(sk);
                          }}
                          onError={() => {
                            scheduleRetry(
                              sk,
                              camera.camera_id,
                              camera.edge_device_id,
                              'Failed to load stream',
                              preferredMjpegModeForCamera(camera.camera_id, currentViewKind),
                              currentViewKind,
                            );
                          }}
                        />
                      )}
                      {!evidenceMode && !unavailableState && !isRunning && !stream?.loading && (
                        <Box
                          sx={{
                            position: 'absolute',
                            inset: 0,
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            zIndex: 3,
                            pointerEvents: 'none',
                          }}
                        >
                          <Button
                            size="small"
                            variant="contained"
                            startIcon={<PlayIcon />}
                            data-testid={!shouldAutoStart ? 'live-view-manual-start' : 'live-view-start-live'}
                            sx={{ pointerEvents: 'auto' }}
                            onClick={(event) => {
                              event.stopPropagation();
                              startLiveStreamForCamera(camera, currentViewKind);
                            }}
                          >
                            {LIVE_VIEW_START_LABEL}
                          </Button>
                        </Box>
                      )}
                    </Box>

                    <LiveViewCameraPatternPanel
                      camera={camera}
                      patternLink={patternLink}
                      onNavigate={navigate}
                      viewKind={currentViewKind}
                      showTechnicalIds={showTechnicalIds}
                      annotatedOverlayAvailable={
                        evidenceMode || (currentViewKind === 'annotated' && isRunning && !unavailableState)
                      }
                    />

                    {!patternLink?.patterns?.length && patternContextError ? (
                      <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                        <Typography variant="caption" color="text.secondary">
                          {patternContextError}
                        </Typography>
                        <Button size="small" variant="text" onClick={() => refreshPatternContext()}>
                          Retry
                        </Button>
                      </Box>
                    ) : null}
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
        </Box>
      )}

      <LiveViewFullscreenDialog
        open={viewerOpen}
        fullScreen={viewerFullScreen}
        title={viewerTitle}
        camera={viewerCamera}
        streamKey={viewerStreamKey}
        viewKind={viewerViewKind}
        stream={viewerStream}
        unavailableState={viewerUnavailableState}
        zoom={zoom}
        pan={pan}
        stoppedStreamKeys={stoppedStreamKeys}
        overlay={null}
        evidenceContent={
          viewerCamera
          && usesAnnotatedEvidenceMode(viewerViewKind)
          && !viewerUnavailableState ? (
            <AnnotatedEvidencePanel
              cameraId={viewerCamera.camera_id}
              edgeDeviceId={viewerCamera.edge_device_id}
              cameraName={viewerCamera.name}
              active={viewerOpen}
              showTechnicalIds={showTechnicalIds}
            />
          ) : null
        }
        onClose={closeViewer}
        onToggleFullScreen={setViewerFullScreen}
        onZoomChange={setZoom}
        onPanChange={setPan}
        onResetZoomPan={() => {
          setZoom(1);
          setPan({ x: 0, y: 0 });
        }}
        onWheel={onViewerWheel}
        onPointerDown={onViewerPointerDown}
        onPointerMove={onViewerPointerMove}
        onPointerUp={onViewerPointerUp}
        onStreamLoad={() => {
          if (viewerStreamKey) clearRetry(viewerStreamKey);
        }}
        onStreamError={() => {
          if (viewerStreamKey && viewerCamera) {
            scheduleRetry(
              viewerStreamKey,
              viewerCamera.camera_id,
              viewerCamera.edge_device_id,
              'Failed to load stream',
              preferredMjpegModeForCamera(viewerCamera.camera_id, viewerViewKind),
              viewerViewKind,
            );
          }
        }}
        onStop={() => {
          if (viewerCamera) {
            stopStream(viewerCamera.camera_id, viewerCamera.edge_device_id, viewerViewKind);
          }
        }}
        onStart={() => {
          if (!viewerStreamKey || !viewerCamera) return;
          setStopped(viewerStreamKey, false);
          startedStreamKeysRef.current.add(viewerStreamKey);
          void startStream(
            viewerCamera.camera_id,
            viewerCamera.edge_device_id,
            preferredMjpegModeForCamera(viewerCamera.camera_id, viewerViewKind),
            viewerViewKind,
          );
        }}
      />
    </Box>
  );
};

export default LiveView;
